/**
 * Fresh ambient context for Ella Radio continue/generate calls.
 * Fetches live weather + time-of-day (including bedtime wind-down) on each refill.
 */

import maestroRadioService from '../services/maestroRadioService';
import { ellaRadioSessionRefs } from './ellaRadioSessionRefs';

export interface AmbientSliders {
  energy?: number;
  acoustic?: number;
  valence?: number;
  vocal?: number;
}

export function applyAmbientSlidersToTuning(
  baseTuning: Record<string, unknown>,
  sliders?: AmbientSliders | null,
): Record<string, unknown> {
  if (!sliders) return baseTuning;
  const t = { ...baseTuning };

  if (t.ambientEnergyOverride == null && sliders.energy != null) {
    t.energyLevel = sliders.energy;
  }
  if (t.ambientVocalOverride == null && sliders.vocal != null) {
    t.vocalWeight = sliders.vocal;
  }
  if (t.ambientAcousticOverride == null && sliders.acoustic != null) {
    // Higher acoustic → calmer tempo feel
    t.tempoFeel = Math.max(10, Math.round(100 - sliders.acoustic * 0.4));
  }
  if (t.ambientValenceOverride == null && sliders.valence != null) {
    t.variety = sliders.valence;
  }

  return t;
}

export function ambientContextToGenerateOpts(ctx: Record<string, any> | null | undefined): Record<string, unknown> {
  const refs = ellaRadioSessionRefs;
  const opts: Record<string, unknown> = {};
  if (!refs.timeAware && !refs.weatherAware) return opts;

  const tzOffset = new Date().getTimezoneOffset() / -60;
  if (refs.timeAware) {
    opts.timeAware = true;
    opts.tzOffset = tzOffset;
    if (refs.bedtimeHour != null) opts.bedtimeHour = refs.bedtimeHour;
  }

  if (!ctx) return opts;

  if (refs.weatherAware && ctx.weather) {
    opts.weatherAware = true;
    opts.weatherDescription = ctx.weather.description;
    opts.weatherTemp = ctx.weather.tempC ?? ctx.weather.temp;
    opts.weatherCondition = ctx.weather.condition;
  }

  if (refs.timeAware && ctx.timeOfDay) {
    opts.timeOfDayLabel = ctx.timeOfDay.label;
    opts.timeOfDayPeriod = ctx.timeOfDay.period;
  }

  if (ctx.combinedMoodSuggestion) opts.ambientMoodSuggestion = ctx.combinedMoodSuggestion;
  if (ctx.combinedLabel) opts.ambientMoodLabel = ctx.combinedLabel;

  if (ctx.defaultAmbientSliders) {
    opts.ambientEnergy = ctx.defaultAmbientSliders.energy;
    opts.ambientAcoustic = ctx.defaultAmbientSliders.acoustic;
    opts.ambientValence = ctx.defaultAmbientSliders.valence;
    opts.ambientVocal = ctx.defaultAmbientSliders.vocal;
  }

  if (refs.locationCity) {
    opts.locationCity = refs.locationCity;
    if (refs.locationRegion) opts.locationRegion = refs.locationRegion;
  }

  return opts;
}

/** Stable fingerprint for detecting weather/time mood changes between songs. */
export function buildAmbientSignature(opts: Record<string, unknown>): string {
  const weather = opts.weatherCondition ?? '';
  const time = opts.timeOfDayLabel ?? '';
  const mood = opts.ambientMoodLabel ?? '';
  const energy = opts.ambientEnergy ?? '';
  return `${weather}|${time}|${mood}|${energy}`;
}

/** Fetch live ambient from the API — used on every continue/refill. */
export async function fetchFreshEllaContinueOpts(
  options?: { activeSession?: boolean },
): Promise<Record<string, unknown>> {
  const refs = ellaRadioSessionRefs;
  if (!refs.timeAware && !refs.weatherAware) return {};

  const tzOffset = new Date().getTimezoneOffset() / -60;

  try {
    const lat = refs.weatherAware ? refs.lat : null;
    const lon = refs.weatherAware ? refs.lon : null;
    const ctx = await maestroRadioService.getAmbientContext(
      lat,
      lon,
      tzOffset,
      refs.bedtimeHour,
      options?.activeSession === true,
    );
    return ambientContextToGenerateOpts(ctx);
  } catch (e) {
    console.warn('[ellaRadioAmbient] fresh ambient fetch failed:', e);
    const fallback: Record<string, unknown> = {};
    if (refs.timeAware) {
      fallback.timeAware = true;
      fallback.tzOffset = tzOffset;
      if (refs.bedtimeHour != null) fallback.bedtimeHour = refs.bedtimeHour;
    }
    if (refs.locationCity) {
      fallback.locationCity = refs.locationCity;
      if (refs.locationRegion) fallback.locationRegion = refs.locationRegion;
    }
    return fallback;
  }
}
